import { useEffect, useMemo, useState } from 'react';
import { Button, Space } from 'antd';
import { useNavigate, useSearchParams } from 'react-router';
import _ from 'lodash';
import UserService from 'services/collaborator_user';
import { useAuth } from 'contexts/auth';
import { useApp } from 'contexts/app';
import { formatCurrency } from 'utils/number';

const service = new UserService();

export const permissions = [
  { label: 'Quản lý sản phẩm', value: 'product.all' },
  { label: 'Quản lý đơn hàng', value: 'order.all' },
  { label: 'Quản lý khách hàng', value: 'customer.all' },
  { label: 'Quản lý công nợ', value: 'debt.all' },
  { label: 'Quản lý voucher', value: 'voucher.all' },
  { label: 'Cấu hình shop', value: 'setting.all' },
];

export const useUserList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({});
  const [showAddUser, setShowAddUser] = useState(false);
  const [showUpdateUser, setShowUpdateUser] = useState(false);
  const [updateUser, setUpdateUser] = useState();

  const { user } = useAuth();
  const { showLoading, message } = useApp();

  const onSearch = async (nextFilter = {}) => {
    const list = await service.list({ info: nextFilter?.info });
    setData(Array.isArray(list) ? list : []);
  };

  const delaySearch = useMemo(() => _.debounce(onSearch, 500, { leading: false }), []);

  useEffect(() => {
    return () => {
      delaySearch.cancel();
    };
  }, [delaySearch]);

  useEffect(() => {
    const info = searchParams.get('info') || '';
    setFilter((prev) => {
      if ((prev?.info || '') === info) {
        return prev;
      }
      return { ...prev, info };
    });
  }, [searchParams]);

  useEffect(() => {
    delaySearch(filter || {});
  }, [filter, delaySearch]);

  const setFilterValue = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const loadData = async () => {
    await onSearch(filter || {});
  };

  const deleteUser = async (id) => {
    showLoading(true);
    try {
      await service.removeUser(id);
      await loadData();
    } catch (error) {
      message.error('Không thể xoá');
    } finally {
      showLoading(false);
    }
  };

  const clickUpdateUser = (selectedUser) => {
    setShowUpdateUser(true);
    setUpdateUser(selectedUser);
  };

  const canViewDetail = (record) => {
    return user?.id != record?.id;
  };

  const openDetail = (record) => {
    if (!record?.id) return;
    navigate(`/collaborator/withdraw/${record.id}`);
  };

  const columns = [
    {
      title: 'Tài khoản',
      render: (_, record) => (
        <div>
          <Space direction='vertical'>
            <div>{record.full_name}</div>
            <div>{record.phone}</div>
          </Space>
        </div>
      ),
    },
    {
      title: 'Số dư hoa hồng',
      render: (_, record) => (
        <div>
          <Space>{formatCurrency(record.commission?.current_commission)}</Space>
        </div>
      ),
    },
    {
      title: 'Hoa hồng đang xử lý',
      render: (_, record) => (
        <div>
          <Space>
            <div>{formatCurrency(record.commission?.pending_commission)}</div>
          </Space>
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <Space size='middle'>
          {canViewDetail(record) && (
            <Button type='link' onClick={() => openDetail(record)}>
              Chi tiết
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return {
    data,
    filter,
    columns,
    user,
    showAddUser,
    setShowAddUser,
    showUpdateUser,
    setShowUpdateUser,
    updateUser,
    setUpdateUser,
    setFilterValue,
    loadData,
    clickUpdateUser,
    deleteUser,
    canViewDetail,
    openDetail,
  };
};
